import React from 'react';

import { useGetMemberNotification } from 'entities/notification';
import type { MemberNotificationResponse } from 'entities/notification';
import { useTranslation } from 'react-i18next';
import { ReviewServiceType } from 'shared/types';

import { formatTimeAgo } from '../lib';
import { WriteReviewButton } from './WriteReviewButton';

type NotificationType = MemberNotificationResponse['notifications'][number]['notificationType'];

const WRITE_NOTIFICATION_TYPES: NotificationType[] = [
  'PROVISIONAL_CONTRACT_COMPLETE',
  'MAIN_CONTRACT_COMPLETE',
  'MAIN_CONTRACT_REVIEW_REMIND',
];

export function Notification({
  onOpenReviewDialog,
}: {
  onOpenReviewDialog: (orderId: number, reviewTargetServiceType: ReviewServiceType) => void;
}) {
  const { t } = useTranslation('review');
  const { data } = useGetMemberNotification();

  const hasItems = data?.notifications && data.notifications.length > 0;

  return (
    <section className="flex h-[calc(100vh-100px)] max-h-[510px] w-[calc(100vw-32px)] max-w-[480px] flex-col rounded-[20px] border border-[#EAEAEA] bg-white shadow-[0_4px_22px_rgba(0,0,0,0.12)] md:h-[510px] md:w-[480px]">
      <div className="rounded-t-[20px] bg-white px-[20px] py-[16px] md:px-[30px] md:py-[31px]">
        <h2 className="text-[16px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222]">
          {t('notifications.title')}
        </h2>
      </div>

      <hr className="h-px w-full bg-[#EAEAEA]" />

      <div className="flex-1 overflow-auto">
        {hasItems ? (
          data.notifications.map(
            ({ description, createdAt, isUnread, notificationType, orderId, reviewTargetServiceType }, index) => {
              return (
                <div key={index} className="flex w-full flex-col gap-[14px] p-[16px_20px] last:mb-0 md:p-[22px_29px]">
                  <div className="flex w-full items-start justify-between gap-[10px]">
                    <p className="min-w-0 flex-1 text-[15px] font-medium leading-[1.5] tracking-[-0.022em] text-[#222222]">
                      {description}
                    </p>

                    {/* 데스크탑 한정: 시간/dot 우측 inline */}
                    <div className="hidden shrink-0 items-center gap-[6px] whitespace-nowrap md:flex">
                      <span className="text-[13px] font-medium leading-[1.5] tracking-[-0.022em] text-[#BEBEBE]">
                        {formatTimeAgo(createdAt, t)}
                      </span>

                      {isUnread && <span className="h-[6px] w-[6px] rounded-full bg-[#FF2D2E]" />}
                    </div>
                  </div>

                  {/* 모바일 한정: 버튼 + 시간/dot 한 줄, 시간 우측 끝 */}
                  <div className="flex w-full items-center justify-between md:hidden">
                    {WRITE_NOTIFICATION_TYPES.includes(notificationType) ? (
                      <WriteReviewButton
                        orderId={orderId}
                        onOpenReviewDialog={() => onOpenReviewDialog(orderId, reviewTargetServiceType)}
                      />
                    ) : (
                      <span />
                    )}
                    <div className="flex items-center gap-[6px] whitespace-nowrap">
                      <span className="text-[13px] font-medium leading-[1.5] tracking-[-0.022em] text-[#BEBEBE]">
                        {formatTimeAgo(createdAt, t)}
                      </span>

                      {isUnread && <span className="h-[6px] w-[6px] rounded-full bg-[#FF2D2E]" />}
                    </div>
                  </div>

                  {/* 데스크탑 한정: 버튼 별도 줄 (기존 구조) */}
                  {WRITE_NOTIFICATION_TYPES.includes(notificationType) && (
                    <div className="hidden items-center gap-[10px] md:flex">
                      <WriteReviewButton
                        orderId={orderId}
                        onOpenReviewDialog={() => onOpenReviewDialog(orderId, reviewTargetServiceType)}
                      />
                    </div>
                  )}
                </div>
              );
            },
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-center text-[15px] font-medium leading-[1.5] tracking-[-0.022em] text-[#8E8E8E]">
              {t('notifications.noNotifications')}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
